import { useState } from 'react';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Skeleton from '@mui/material/Skeleton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { ActionLogEntry, ActionStatus } from '../types';

const statusColorMap: Record<ActionStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  pending: 'default',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  cancelled: 'warning',
  blocked: 'warning',
};

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function copyToClipboard(text: string) {
  void navigator.clipboard.writeText(text);
}

function CopyableId({ value }: { value?: string }) {
  if (!value) return <span>—</span>;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
      <Tooltip title="Copy">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(value);
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function ActivityRow({ entry }: { entry: ActionLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <>
      <TableRow
        hover
        onClick={() => hasDetails && setOpen(!open)}
        sx={hasDetails ? { cursor: 'pointer' } : undefined}
      >
        <TableCell sx={{ width: 30, p: 0.5 }}>
          {hasDetails && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
            >
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ width: 140 }}>{formatTimestamp(entry.timestampUtc)}</TableCell>
        <TableCell sx={{ width: 90 }}>{entry.module}</TableCell>
        <TableCell sx={{ width: 80, textTransform: 'capitalize' }}>{entry.action}</TableCell>
        <TableCell sx={{ width: 90 }}>
          <Chip
            label={entry.status}
            color={statusColorMap[entry.status]}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        </TableCell>
        <TableCell sx={{ width: 120 }}>{entry.actorLabel}</TableCell>
        <TableCell sx={{ width: 130 }}>{entry.entityRef}</TableCell>
        <TableCell sx={{ width: 130 }}>
          <CopyableId value={entry.correlationId} />
        </TableCell>
        <TableCell sx={{ width: 130 }}>
          <CopyableId value={entry.operationId} />
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                  {JSON.stringify(entry.details, null, 2)}
                </Typography>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface Props {
  entries: ActionLogEntry[];
  loading?: boolean;
  page?: number;
  rowsPerPage?: number;
  totalRows?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (size: number) => void;
}

export default function ActivityTable({
  entries,
  loading = false,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
}: Props) {
  const showPagination =
    page !== undefined &&
    rowsPerPage !== undefined &&
    totalRows !== undefined &&
    onPageChange !== undefined;

  return (
    <div data-testid="activity-table">
      <Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 30 }} />
                <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Entity</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Correlation ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Operation ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }, (__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      No activity entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => <ActivityRow key={entry.logId} entry={entry} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={(e) => onRowsPerPageChange?.(Number.parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Box>
    </div>
  );
}
